import companyService from '../../services/companyService'
import validators from '../validators'
import {
  CompanyType,
  EstimatedProjectValueBand,
  PlanningApplicationStatus,
  PlanningApplicationType,
  PlanningCategory,
  PlanningClassification,
  allCompanyTypes,
  allEstimatedProjectValueBands,
  allPlanningApplicationStatuses,
  allPlanningApplicationTypes,
  allPlanningCategories,
  allPlanningClassifications,
} from '@leadgen/models/client'
import {
  filterPlanningCategoriesForClassifications,
  getPlanningCategoriesForClassifications,
  planningCategoriesByCompanyTypeMap,
  planningClassificationsByCompanyTypeMap,
} from '../../constants/planningTaxonomy'

export { planningCategoriesByCompanyTypeMap, planningClassificationsByCompanyTypeMap }

export const planningApplicationTypesByCompanyTypeMap: Record<
  CompanyType,
  PlanningApplicationType[]
> = {
  builder: ['listed', 'unlisted'],
  glazier: ['listed', 'unlisted'],
  flooring: ['listed', 'unlisted'],
  kitchen: ['listed', 'unlisted'],
  retail: ['listed', 'unlisted'],
  landscaper: ['listed', 'unlisted'],
  renewables: ['listed', 'unlisted'],
}

class FiltersForm {
  onSubmit = async (values: {
    companyId: string
    companyType: CompanyType
    planningCategories: PlanningCategory[]
    planningClassifications: PlanningClassification[]
    planningApplicationTypes: PlanningApplicationType[]
    planningApplicationStatuses: PlanningApplicationStatus[]
    estimatedProjectValueBands: EstimatedProjectValueBand[]
  }): Promise<void> => {
    const {
      companyId,
      companyType,
      planningCategories,
      planningClassifications,
      planningApplicationTypes,
      planningApplicationStatuses,
      estimatedProjectValueBands,
    } = values

    const planningCategoriesByCompanyType = planningCategoriesByCompanyTypeMap[companyType]
    const planningClassificationsByCompanyType =
      planningClassificationsByCompanyTypeMap[companyType]
    const planningApplicationTypesByCompanyType =
      planningApplicationTypesByCompanyTypeMap[companyType]
    const availablePlanningCategories = getPlanningCategoriesForClassifications(
      planningClassifications,
    ).filter(planningCategory => planningCategoriesByCompanyType.includes(planningCategory))
    if (
      !planningCategories.every(planningCategory =>
        availablePlanningCategories.includes(planningCategory),
      )
    ) {
      throw new Error('Failed to save filters. Please try again.')
    }
    if (
      !planningClassifications.every(planningClassification =>
        planningClassificationsByCompanyType.includes(planningClassification),
      )
    ) {
      throw new Error('Failed to save filters. Please try again.')
    }
    if (
      !planningApplicationTypes.every(planningApplicationType =>
        planningApplicationTypesByCompanyType.includes(planningApplicationType),
      )
    ) {
      throw new Error('Failed to save filters. Please try again.')
    }
    if (
      !planningApplicationStatuses.length ||
      !planningApplicationStatuses.every(planningApplicationStatus =>
        allPlanningApplicationStatuses.includes(planningApplicationStatus),
      )
    ) {
      throw new Error('Failed to save filters. Please try again.')
    }
    return companyService.updateFilters({
      companyId,
      companyType,
      planningCategories,
      planningClassifications,
      planningApplicationTypes,
      planningApplicationStatuses,
      estimatedProjectValueBands,
    })
  }
  validateCompanyTypeField = validators.combineValidators(
    validators.validateRequired({
      errorMessage: 'Please select a company type',
    }),
    validators.validateOneOf<CompanyType>({
      errorMessage: 'Value must be a company type',
      options: allCompanyTypes,
    }),
  )
  validatePlanningCategoriesField = validators.combineValidators(
    validators.validateRequired({
      errorMessage: 'Please select one or more filter categories e.g. Conversions',
    }),
    validators.validateOptionsMulti<PlanningCategory>({
      errorMessage: 'Value must be filter categories',
      options: allPlanningCategories,
    }),
  )
  validatePlanningClassificationsField = validators.combineValidators(
    validators.validateRequired({
      errorMessage: 'Please select one or more planning classifications e.g. Commercial',
    }),
    validators.validateOptionsMulti<PlanningClassification>({
      errorMessage: 'Values must be planning classifications',
      options: allPlanningClassifications,
    }),
  )
  validatePlanningApplicationTypesField = validators.combineValidators(
    validators.validateRequired({
      errorMessage: 'Please select one or more application types e.g. unlisted',
    }),
    validators.validateOptionsMulti<PlanningApplicationType>({
      errorMessage: 'Values must be planning application types',
      options: allPlanningApplicationTypes,
    }),
  )
  validatePlanningApplicationStatusesField = validators.combineValidators(
    validators.validateRequired({
      errorMessage: 'Please select one or more application statuses',
    }),
    validators.validateOptionsMulti<PlanningApplicationStatus>({
      errorMessage: 'Values must be planning application statuses',
      options: allPlanningApplicationStatuses,
    }),
  )
  validateEstimatedProjectValueBandsField = validators.combineValidators(
    validators.validateRequired({
      errorMessage: 'Please select one or more project value bands',
    }),
    validators.validateOptionsMulti<EstimatedProjectValueBand>({
      errorMessage: 'Values must be project value bands',
      options: allEstimatedProjectValueBands,
    }),
  )

  normalizePlanningCategories = (
    companyType: CompanyType,
    planningCategories: PlanningCategory[],
    planningClassifications: PlanningClassification[] = [],
  ): PlanningCategory[] => {
    const planningCategoriesByCompanyType = new Set(planningCategoriesByCompanyTypeMap[companyType])
    return filterPlanningCategoriesForClassifications({
      planningCategories: planningCategories.filter(planningCategory =>
        planningCategoriesByCompanyType.has(planningCategory),
      ),
      planningClassifications,
    })
  }
  getNextPlanningCategories = (
    companyType: CompanyType | null,
    currentPlanningCategories: PlanningCategory[],
    currentPlanningClassifications: PlanningClassification[],
    toggledPlanningCategory: PlanningCategory,
    checked: boolean,
  ): PlanningCategory[] => {
    const nextPlanningCategories = checked
      ? [...currentPlanningCategories, toggledPlanningCategory]
      : currentPlanningCategories.filter(
          planningCategory => planningCategory !== toggledPlanningCategory,
        )

    if (!companyType) {
      return nextPlanningCategories
    }

    return this.normalizePlanningCategories(
      companyType,
      nextPlanningCategories,
      currentPlanningClassifications,
    )
  }
}

export default new FiltersForm()
